import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { isArray, sortBy, uniqBy, xor } from "lodash";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import twilioErrorCodes from "@shared/v2/twilioErrorCodes";
import formatPhoneNumber from "@shared/FormatPhoneNumber";
import { getExternalApi } from "@shared/externalApi";

const getBaseEndpoint = ({ currentUser, isAll, searchOpen, trackingNumbers }) => {
  const basePath = `/Conversation${searchOpen ? "Search" : ""}`;
  let path = `${basePath}/Recent`;
  let method = searchOpen ? "post" : "get";
  if (currentUser.account.isAdmin && trackingNumbers?.length > 0) {
    path = `${basePath}/RecentForPeople/TrackingNumbers`;
    method = "post";
  } else if (currentUser.account.isAdmin && isAll) {
    path = `${basePath}/RecentForOrganization`;
    if (searchOpen) method = "post";
  } else if (currentUser.account.isAdmin) {
    path = `${basePath}/RecentForPeople`;
    method = "post";
  }
  return { path, method };
};

const getConversationTypes = (currentTab) => {
  switch (currentTab) {
    case "group":
      return ["groupSms"];
    default:
      return ["sms", "groupSms"];
  }
};

export const transformConversation = (conversation) => {
  const { conversationUsers, dateCreated, dateUpdated } = conversation;
  const sortedConversationUsers = sortBy(
    conversationUsers.map((u) => ({
      ...u,
      alias: u.alias || formatPhoneNumber(u.address),
    })),
    "alias",
  );
  return {
    ...conversation,
    conversationOwnerPersonUuids: conversationUsers
      .filter((u) => u.isConversationOwner)
      .map((u) => u.personUuid),
    conversationUsers: sortedConversationUsers,
    friendlyName: sortedConversationUsers
      .map((u) => u.alias)
      .join(sortedConversationUsers.length > 2 ? ", " : " & "),
    dateUpdated: dateUpdated || dateCreated || new Date().toISOString(),
  };
};

export const agentFiltersKey = (userUuid) => `inbox_agent_filters_${userUuid}`;

export const validAgentFilters = (allFilterAgents, currentPersonUuid) => {
  if (!allFilterAgents) return undefined;
  return [
    "all",
    `person-${currentPersonUuid}`,
    ...(allFilterAgents.agents || []).map((agent) => `person-${agent.personUuid}`),
    ...(allFilterAgents.trackingNumbers || []).map((tn) => `trackingNumber-${tn.number}`),
  ];
};

export const persistedAgentFilters = (userUuid, validFilters) => {
  if (!userUuid) return ["all"];
  try {
    const saved = JSON.parse(localStorage.getItem(agentFiltersKey(userUuid)));
    if (!Array.isArray(saved)) return ["all"];
    const kept = validFilters ? saved.filter((value) => validFilters.includes(value)) : saved;
    return kept.length > 0 ? kept : ["all"];
  } catch {
    return ["all"];
  }
};

export const initialState = {
  accountSenderPlaceholders: [],
  aiFilters: ["all"],
  aiUnreadCount: 0,
  allFilterAgents: [],
  allUnreadCount: 0,
  conflictConversation: null,
  conversations: [],
  conversationsPage: 1,
  conversationsPerPage: 25,
  currentTab: "all",
  draftTextMessages: {}, // Stores in-progress text messages for each text thread
  errors: null,
  focusedConversation: { eventType: "blank" },
  groupUnreadCount: 0,
  hasMore: false,
  hasMoreMessages: false,
  hideAi: false,
  isOptedOut: false,
  lastSearch: "",
  loadingConversations: false,
  loadingMessages: false,
  messagesErrors: null,
  messagesErrorsLink: null,
  messagesPage: 1,
  messagesPerPage: 25,
  messagingServiceUrl: "",
  newSmsMediaFile: null,
  recipients: {},
  searchOpen: false,
  searchText: "",
  selectedAgentFilters: ["all"],
  selectedMessages: [],
  sendingTextMessage: false,
  twilioErrorMessage: {},
};

const getUnreadCounts = createAsyncThunk("messaging/getUnreadCounts", (_, thunkApi) => {
  const { messagingServiceUrl } = thunkApi.getState().messaging;
  const messagingApi = getExternalApi(messagingServiceUrl);
  return Promise.all([
    messagingApi
      .get("/User/UnreadMessageCount", {
        params: { conversationTypes: ["sms", "groupSms"], isAiControlled: true },
      })
      .then((res) => res.data)
      .catch(() => 0),
    messagingApi
      .get("/User/UnreadMessageCount", { params: { conversationTypes: ["groupSms"] } })
      .then((res) => res.data)
      .catch(() => 0),
  ]).then(([aiUnreadCount, groupUnreadCount]) => ({
    aiUnreadCount,
    groupUnreadCount,
  }));
});

const getConversations = createAsyncThunk("messaging/getConversations", (_, thunkApi) => {
  const { currentUser } = thunkApi.getState().layout;
  const {
    aiFilters,
    conversationsPage,
    conversationsPerPage,
    currentTab,
    hideAi,
    messagingServiceUrl,
    searchOpen,
    searchText,
    selectedAgentFilters,
    unreadOnly,
  } = thunkApi.getState().messaging;
  const isAiControlled = currentTab === "all" && hideAi ? false : undefined;
  const selectedFilters =
    selectedAgentFilters?.[0] === "all" ? undefined : selectedAgentFilters?.filter((o) => o !== "all");

  const prefixFilter = (prefix) =>
    selectedFilters?.filter((o) => o.startsWith(prefix))?.map((o) => o.replace(prefix, ""));

  const agentUuids = prefixFilter("person-");
  const trackingNumbers = prefixFilter("trackingNumber-");
  const selectedFiltersObject = {
    personUuids: agentUuids,
    trackingNumbers: trackingNumbers,
  };
  const baseEndpoint = getBaseEndpoint({ currentUser, isAll: !selectedFilters, searchOpen, trackingNumbers });

  let requestBody = {};
  if (trackingNumbers?.length > 0) {
    requestBody = {
      searchTerm: searchOpen ? searchText : undefined,
      conversationTypes: getConversationTypes(currentTab),
      ...selectedFiltersObject,
    };
  } else if (searchOpen) {
    requestBody = {
      searchTerm: searchOpen ? searchText : undefined,
      conversationTypes: getConversationTypes(currentTab),
      personUuids: agentUuids,
    };
  } else {
    requestBody = agentUuids;
  }

  const messagingApi = getExternalApi(messagingServiceUrl);
  return messagingApi({
    method: baseEndpoint.method,
    url: `${baseEndpoint.path}/${conversationsPage}/${conversationsPerPage}`,
    data: requestBody,
    params: {
      conversationTypes: searchOpen ? undefined : getConversationTypes(currentTab),
      isAiControlled: currentTab === "ai" || isAiControlled,
      unReadOnly: (currentTab !== "ai" && unreadOnly) || undefined,
      aiConversationStatus: currentTab !== "ai" || aiFilters.includes("all") ? undefined : aiFilters,
    },
    signal: thunkApi.signal,
  })
    .then((res) => res.data)
    .catch((err) => thunkApi.rejectWithValue(err.response?.data));
});

const getPhoneStatuses = createAsyncThunk("messaging/getPhoneStatuses", (_, thunkApi) => {
  const {
    messaging: { focusedConversation },
  } = thunkApi.getState();
  return Promise.all(
    focusedConversation.conversationUsers
      ?.filter((u) => !u.isConversationOwner)
      .map((u) => caseTransformingAxios.get(`/api/v4/leads/${u.personUuid}/phones`)),
  )
    .then((responses) =>
      responses
        .map((res) =>
          res.data.find((phone) =>
            focusedConversation.conversationUsers.find((u) => u.address === phone.canonicalNumber),
          ),
        )
        .filter(Boolean),
    )
    .catch(() => {});
});

const getMessages = createAsyncThunk("messaging/getMessages", (opts, thunkApi) => {
  const {
    messaging: { focusedConversation, messagesPage, messagesPerPage, messagingServiceUrl },
  } = thunkApi.getState();
  const messagingApi = getExternalApi(messagingServiceUrl);

  return messagingApi
    .get(
      `/ConversationMessages/${opts?.uuid || focusedConversation.uuid}/${messagesPage}/${messagesPerPage}`,
      {
        signal: thunkApi.signal,
      },
    )
    .then((res) => res.data)
    .catch((err) => thunkApi.rejectWithValue(err.response?.data));
});

const markAsRead = createAsyncThunk("messaging/markAsRead", (_, thunkApi) => {
  const { focusedConversation, messagingServiceUrl } = thunkApi.getState().messaging;
  const messagingApi = getExternalApi(messagingServiceUrl);
  // This will trigger a SignalR message "ResetConversationUnreadMessageCount"
  return Promise.all([
    messagingApi.put(`/ConversationUser/ResetUnreadCount/${focusedConversation.uuid}`),
    caseTransformingAxios.post("/interactions/mark_conversation_as_read", {
      conversationUuid: focusedConversation.uuid,
    }),
  ]).catch(console.log);
});

const sendMessage = createAsyncThunk("messaging/sendMessage", (opts, thunkApi) => {
  const {
    draftTextMessages,
    focusedConversation,
    newSmsMediaFile,
    recipients,
    messagingServiceUrl,
    conversations,
  } = thunkApi.getState().messaging;
  const messagingApi = getExternalApi(messagingServiceUrl);
  let promise = Promise.resolve({});

  if (focusedConversation.uuid === "temp") {
    let users = [];
    users = Object.values(recipients).map(({ recipient, number }) => ({
      address: number.value,
      personUuid: recipient.value,
      isConversationOwner: false,
    }));

    promise = messagingApi
      .post(
        "/Conversation/WithUsers",
        {
          conversationType: "groupSms",
          isChannel: false,
          users,
        },
        { ...opts, signal: thunkApi.signal },
      )
      .catch((err) => {
        if (err.response?.status === 409 && typeof err.response?.data === "object") {
          return { data: { ...err.response.data, isConflict: true } };
        }
        throw err;
      });
  }

  return promise
    .then(({ data: newConversation }) => {
      const draftMessage = draftTextMessages[focusedConversation.uuid];
      const formData = new FormData();
      formData.append("conversationUuid", newConversation?.uuid || focusedConversation.uuid);
      formData.append("messageType", "standard");
      if (draftMessage) formData.append("message", draftMessage);
      if (newSmsMediaFile) formData.append("attachment", newSmsMediaFile);

      return messagingApi.post("/Message/Sms", formData).then((res) => {
        if (newConversation) {
          const conversation = conversations.find((c) => c.uuid === newConversation.uuid) || {
            conversationOwnerPersonUuids: [res.data.createdByUserPersonUuid],
          };
          thunkApi.dispatch(
            selectNewConversation({ ...conversation, ...newConversation, mostRecentMessage: res.data }),
          );
        }
        return res;
      });
    })
    .catch((err) => {
      let errors = [err?.response?.data || "There was a problem sending your message. Please try again."];
      return thunkApi.rejectWithValue(errors);
    });
});

const receiveSignalRNewMessageWithConversationFetch = createAsyncThunk(
  "messaging/receiveSignalRNewMessageWithConversationFetch",
  async (message, thunkApi) => {
    const {
      messaging: { conversations, currentTab, messagingServiceUrl },
    } = thunkApi.getState();
    const messageConversationUuid = message.conversationUuid;
    const existingConversation = conversations.find((c) => c.uuid === messageConversationUuid);

    if (existingConversation) {
      thunkApi.dispatch(receiveSignalRNewMessage(message));
      return;
    }

    const messagingApi = getExternalApi(messagingServiceUrl);

    try {
      const { data: conversation } = await messagingApi.get(`/Conversation/${messageConversationUuid}`);
      const isGroupConversation = conversation?.conversationType === "groupSms";
      const isAiMessage = conversation?.isAiControlled;

      if (currentTab === "group" && !isGroupConversation) {
        thunkApi.dispatch(incrementUnreadCountsForUntrackedConversation({ isAi: isAiMessage }));
        return;
      }

      thunkApi.dispatch(receiveSignalRNewConversation(conversation));
      thunkApi.dispatch(receiveSignalRNewMessage(message));
    } catch (_error) {
      thunkApi.dispatch(receiveSignalRNewMessage(message));
    }
  },
);

const markAllAsReadByConversationTypes = createAsyncThunk(
  "messaging/markAllAsReadByConversationType",
  (_, thunkApi) => {
    const { currentTab, hideAi, messagingServiceUrl } = thunkApi.getState().messaging;
    const messagingApi = getExternalApi(messagingServiceUrl);
    let isAiControlled = undefined;
    if (currentTab === "ai") isAiControlled = true;
    else if (hideAi) isAiControlled = false;
    return messagingApi
      .put(`/User/ResetUnreadCount`, null, {
        params: {
          conversationTypes: getConversationTypes(currentTab),
          isAiControlled,
        },
        signal: thunkApi.signal,
      })
      .then((res) => res.data);
  },
);

export const messagingSlice = createSlice({
  name: "messaging",
  initialState,
  reducers: {
    addRecipient: (state, { payload: { recipient, number } = {} }) => {
      state.recipients[recipient.value] = { recipient, number };
    },
    removeRecipient: (state, { payload: { recipient } = {} }) => {
      delete state.recipients[recipient.value];
    },
    focusConversation: (state, { payload }) => {
      if (state.focusedConversation.uuid !== payload.uuid || payload.uuid === "temp") {
        state.isOptedOut = false;
        state.selectedMessages = [];
        state.messagesPage = 1;
        state.hasMoreMessages = false;
        state.messagesErrors = null;
        state.messagesErrorsLink = null;
      }
      state.focusedConversation = payload;
      state.conflictConversation = null;
    },
    nextConversationPage: (state) => {
      state.conversationsPage = state.conversationsPage + 1;
    },
    nextMessagesPage: (state) => {
      state.messagesPage = state.messagesPage + 1;
    },
    selectNewConversation: (state, { payload }) => {
      state.conversations = uniqBy([transformConversation(payload), ...state.conversations], "uuid");
      state.focusedConversation = payload;
      state.conflictConversation = null;
    },
    receiveSignalRNewConversation: (state, { payload }) => {
      state.conversations = uniqBy([transformConversation(payload), ...state.conversations], "uuid");
    },
    receiveSignalRNewMessage: (state, { payload }) => {
      if (state.focusedConversation.uuid === payload.conversationUuid) {
        state.selectedMessages = uniqBy([...state.selectedMessages, payload], "uuid");
      }
      const conversation = state.conversations.find((o) => o.uuid === payload.conversationUuid);
      if (conversation) {
        conversation.mostRecentMessage = payload;
        conversation.dateUpdated = payload.dateCreated;
        if (state.focusedConversation.uuid !== payload.conversationUuid) {
          if (!conversation.userUnreadMessageCount) conversation.userUnreadMessageCount = 0;
          conversation.userUnreadMessageCount += 1;
          state.allUnreadCount += 1;
          if (conversation.conversationUsers.length > 2) state.groupUnreadCount += 1;
          if (conversation.isAiControlled) state.aiUnreadCount += 1;
        }
      }
    },
    incrementUnreadCountsForUntrackedConversation: (state, { payload = {} }) => {
      state.allUnreadCount += 1;
      if (payload.isAi) state.aiUnreadCount += 1;
    },
    receiveSignalRMessageResetCount: (state, { payload = {} }) => {
      const conversation = state.conversations.find((o) => o.uuid === payload.conversationUuid);
      state.allUnreadCount = Math.max(state.allUnreadCount - payload.priorCount, 0);
      if (conversation) {
        conversation.userUnreadMessageCount = 0;
        if (conversation.conversationUsers.length > 2) {
          state.groupUnreadCount = Math.max(state.groupUnreadCount - payload.priorCount, 0);
        }
        if (conversation.isAiControlled) {
          state.aiUnreadCount = Math.max(state.aiUnreadCount - payload.priorCount, 0);
        }
      }
    },
    receiveSignalRMessageStatusFailure: (state, { payload }) => {
      const message = state.selectedMessages.find((o) => o.uuid === payload.messageUuid);
      if (message?.deliveryStatuses) {
        message.deliveryStatuses = message.deliveryStatuses.map((s) =>
          s.conversationUserUuid === payload.conversationUserUuid ? payload : s,
        );
      }
    },
    setSearchOpen: (state, { payload }) => {
      state.searchOpen = payload;
      state.searchText = "";
    },
    setSearchText: (state, { payload }) => {
      state.searchText = payload;
      state.conversationsPage = 1;
    },
    setTab: (state, { payload }) => {
      state.currentTab = payload;
      state.conversationsPage = 1;
    },
    setMedia: (state, { payload }) => {
      state.newSmsMediaFile = payload;
    },
    updateAgentFilters: (state, { payload: { value, replace } }) => {
      if (replace) {
        state.selectedAgentFilters = [value];
      } else {
        state.selectedAgentFilters = xor(
          state.selectedAgentFilters.filter((o) => o !== "all"),
          [value],
        );
        if (state.selectedAgentFilters.length === 0) state.selectedAgentFilters.push("all");
      }
      state.conversationsPage = 1;
    },
    updateDraft: (state, { payload }) => {
      state.draftTextMessages[state.focusedConversation.uuid] = payload;
    },
    setConflictConversation: (state, { payload }) => {
      state.conflictConversation = payload;
    },
    removeConflictConversation: (state) => {
      state.conflictConversation = null;
    },
    setErrors: (state, { payload }) => {
      if (payload.errors) state.errors = payload.errors;
      if (payload.messagesErrors) state.messagesErrors = payload.messagesErrors;
    },
    setHideAi: (state, { payload }) => {
      state.conversationsPage = 1;
      state.hideAi = payload;
    },
    setUnreadOnly: (state, { payload }) => {
      state.conversationsPage = 1;
      state.unreadOnly = payload;
    },
    toggleAiFilter: (state, { payload }) => {
      state.conversationsPage = 1;
      if (payload === "all") {
        state.aiFilters = ["all"];
      } else {
        if (state.aiFilters.includes("all")) {
          state.aiFilters = [payload];
        } else if (state.aiFilters.includes(payload)) {
          state.aiFilters = state.aiFilters.filter((o) => o !== payload);
        } else {
          state.aiFilters.push(payload);
        }
        if (state.aiFilters.length === 0) state.aiFilters.push("all");
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUnreadCounts.fulfilled, (state, { payload }) => {
      state.aiUnreadCount = payload.aiUnreadCount;
      state.groupUnreadCount = payload.groupUnreadCount;
    });
    builder.addCase(getConversations.pending, (state) => {
      state.loadingConversations = true;
    });
    builder.addCase(getConversations.fulfilled, (state, { payload }) => {
      state.loadingConversations = false;
      state.errors = null;
      state.conversations = [
        ...(state.conversationsPage === 1 ? [] : state.conversations),
        ...(payload.conversations || []).map(transformConversation),
      ];
      state.hasMore = payload.conversations?.length >= state.conversationsPerPage;
      if (state.currentTab === "all") state.allUnreadCount = payload.totalUserUnreadMessageCount;
      if (state.currentTab === "group") state.groupUnreadCount = payload.totalUserUnreadMessageCount;
      if (state.currentTab === "ai") state.aiUnreadCount = payload.totalUserUnreadMessageCount;
    });
    builder.addCase(getConversations.rejected, (state, { payload, error }) => {
      if (error?.name === "AbortError") return;
      state.loadingConversations = false;
      state.errors = payload?.errorMessage ? [payload.errorMessage] : ["Whoops, something went wrong."];
    });

    builder
      .addCase(getPhoneStatuses.pending, (state) => {
        state.isOptedOut = false;
      })
      .addCase(getPhoneStatuses.fulfilled, (state, { payload }) => {
        const isOptedOut = Boolean(payload.find((s) => s.smsAccountUnsubscribed || s.smsSoftUnsubscribed));
        state.twilioErrorMessage = payload.reduce((acc, s) => {
          acc[s.canonicalNumber] = twilioErrorCodes[s.errorCode];
          return acc;
        }, {});
        state.isOptedOut = isOptedOut;
      });

    builder
      .addCase(getMessages.pending, (state) => {
        state.loadingMessages = true;
      })
      .addCase(getMessages.fulfilled, (state, { payload }) => {
        state.loadingMessages = false;
        state.hasMoreMessages = (payload.items?.length || 0) >= state.messagesPerPage;
        state.selectedMessages = [
          ...(payload.items || []).reverse(),
          ...(state.messagesPage === 1 ? [] : state.selectedMessages),
        ];
      })
      .addCase(getMessages.rejected, (state, { payload, error }) => {
        state.loadingMessages = false;
      });

    builder.addCase(sendMessage.pending, (state) => {
      state.sendingTextMessage = true;
      state.messagesErrors = null;
      state.messagesErrorsLink = null;
    });
    builder.addCase(sendMessage.fulfilled, (state, { payload }) => {
      const focusedConvo = payload.data.conversation ? payload.data.conversation : state.focusedConversation;

      if (payload.data.conversation) {
        state.conversations = state.conversations.filter(
          (convo) => convo.uuid !== payload.data.conversation.uuid,
        );
      }

      state.sendingTextMessage = false;
      state.draftTextMessages[focusedConvo.uuid] = "";
      state.draftTextMessages["temp"] = "";
      state.newSmsMediaFile = null;
      state.recipients = {};
      state.selectedMessages = uniqBy([...state.selectedMessages, payload.data], "uuid");

      state.focusedConversation = focusedConvo;
    });
    builder.addCase(sendMessage.rejected, (state, { payload }) => {
      state.sendingTextMessage = false;

      if (!isArray(payload)) {
        state.messagesErrors = ["There was a problem sending your message. Please try again."];
        return;
      }
      const payloadErr = payload[0];
      if (Object.prototype.hasOwnProperty.call(payloadErr, "isTrackingNumber")) {
        state.messagesErrors = [
          "This number is already linked to another contact with an active conversation. To resolve, remove the number from one contact or merge the contacts in CRM.",
        ];
        state.messagesErrorsLink = `/people/${payloadErr?.conversationUsers[1]?.slug}/edit`;
      } else {
        state.messagesErrors = payload;
      }
    });

    builder
      .addCase(markAllAsReadByConversationTypes.pending, (state) => {
        state.loadingConversations = true;
      })
      .addCase(markAllAsReadByConversationTypes.fulfilled, (state) => {
        state.loadingConversations = false;
        if (state.currentTab === "all") {
          state.allUnreadCount = 0;
          state.groupUnreadCount = 0;
          state.aiUnreadCount = 0;
        }
        if (state.currentTab === "group") {
          state.allUnreadCount = Math.max(0, state.allUnreadCount - state.groupUnreadCount);
          state.groupUnreadCount = 0;
        }
        if (state.currentTab === "ai") {
          state.allUnreadCount = Math.max(0, state.allUnreadCount - state.aiUnreadCount);
          state.aiUnreadCount = 0;
        }
        state.conversations = state.conversations.map((conversation) => {
          if (
            (state.currentTab === "all" ||
              (state.currentTab === "group" && conversation.conversationType === "groupSms") ||
              (state.currentTab === "ai" && conversation.isAiControlled)) &&
            conversation.userUnreadMessageCount > 0
          ) {
            return { ...conversation, userUnreadMessageCount: 0 };
          }
          return conversation;
        });
      })
      .addCase(markAllAsReadByConversationTypes.rejected, (state) => {
        state.loadingConversations = false;
      });
  },
});

export {
  getUnreadCounts,
  getConversations,
  getPhoneStatuses,
  getMessages,
  markAsRead,
  receiveSignalRNewMessageWithConversationFetch,
  sendMessage,
  markAllAsReadByConversationTypes,
};

export const {
  addRecipient,
  focusConversation,
  nextConversationPage,
  nextMessagesPage,
  receiveSignalRMessageResetCount,
  receiveSignalRMessageStatusFailure,
  receiveSignalRNewConversation,
  receiveSignalRNewMessage,
  incrementUnreadCountsForUntrackedConversation,
  removeConflictConversation,
  removeRecipient,
  selectNewConversation,
  setConflictConversation,
  setErrors,
  setHideAi,
  setMedia,
  setSearchOpen,
  setSearchText,
  setTab,
  setUnreadOnly,
  toggleAiFilter,
  updateAgentFilters,
  updateDraft,
} = messagingSlice.actions;

export default messagingSlice.reducer;
